import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  Button,
  Input,
  SimpleGrid,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Heading,
  useToast,
  Code,
  HStack,
  VStack,
  Icon,
  Divider,
  InputGroup,
  InputRightAddon,
  Grid,
  GridItem,
  Switch,
} from "@chakra-ui/react";
import { 
  FaUnlink, 
  FaCopy, 
  FaStore, 
  FaSync, 
  FaBoxOpen, 
  FaChartLine, 
  FaShoppingCart, 
  FaCalendarAlt,
  FaKey,
  FaLink,
  FaCogs
} from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";

// سرویس‌های واقعی بک‌اند
import { 
  getGateway, 
  getGatewayStats, 
  importGatewayProducts, 
  getBasalamWebhooks, 
  updateGateway, 
  disconnectGatewayProducts,
  getBasalamCustomerId
} from "../services/gatewayService";
import { toDate, formatDateShamsi, formatDateShamsiShort } from "../utils/date";

// ==========================================
// کامپوننت کارت آماری کوچک و زیبا
// ==========================================
function MiniStatCard({ title, value, helpText, icon, colorScheme }) {
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.200");
  const iconBg = useColorModeValue(`${colorScheme}.50`, "whiteAlpha.100");
  const iconColor = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.300`);
  const labelColor = useColorModeValue("gray.500", "gray.400");
  const valueColor = useColorModeValue("gray.800", "white");
  const helpColor = useColorModeValue("gray.400", "gray.500");

  return (
    <Box
      p={5}
      bg={bg}
      borderRadius="2xl"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="0 4px 20px -2px rgba(0,0,0,0.02)"
      transition="all 0.2s"
      _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
    >
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontSize="sm" color={labelColor} fontWeight="bold">
          {title}
        </Text>
        <Flex w="36px" h="36px" borderRadius="xl" bg={iconBg} color={iconColor} align="center" justify="center">
          <Icon as={icon} boxSize={4} />
        </Flex>
      </Flex>
      <Text fontSize="2xl" fontWeight="black" color={valueColor} letterSpacing="tight">
        {value}
      </Text>
      {helpText && (
        <Text fontSize="xs" color={helpColor} mt={1}>
          {helpText}
        </Text>
      )}
    </Box>
  );
}

// ==========================================
// صفحه اصلی پروفایل درگاه
// ==========================================
export default function GatewayProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Color Mode
  const bgPage = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.200");
  const codeBg = useColorModeValue("slate.50", "whiteAlpha.50");

  const [customSiteUrl, setCustomSiteUrl] = useState("");
  const [customCallbackUrl, setCustomCallbackUrl] = useState("");
  const [customProductsUrl, setCustomProductsUrl] = useState("");
  const [customStockUpdateUrl, setCustomStockUpdateUrl] = useState("");
  const [isDirty, setIsDirty] = useState(false);

  // Mutations
  const importMutation = useMutation(
    () => importGatewayProducts(id),
    {
      onSuccess: (res) => {
        const { imported = 0, updated = 0 } = res?.data || {};
        toast({
          title: "همگام‌سازی انجام شد",
          description: res.message,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top-right"
        });
        queryClient.invalidateQueries(["gateway-stats", id]);
        queryClient.invalidateQueries(["gateway", id]);
      },
      onError: (err) => {
        toast({
          title: "خطا در دریافت اطلاعات",
          description: err.response?.data?.message || err.message,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right"
        });
      },
    }
  );

  const disconnectProductsMutation = useMutation(
    () => disconnectGatewayProducts(id),
    {
      onSuccess: (res) => {
        const { disconnectedCount = 0, deletedProductsCount = 0 } = res?.data || {};
        toast({
          title: "لغو اتصال موفق",
          description: `اتصال ${disconnectedCount} محصول لغو شد. ${deletedProductsCount} محصول یتیم حذف شد.`,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top-right"
        });
        queryClient.invalidateQueries(["gateway-stats", id]);
        queryClient.invalidateQueries(["products"]); 
      },
      onError: (err) => {
        toast({
          title: "خطا در لغو اتصال",
          description: err.response?.data?.message || err.message,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right"
        });
      },
    }
  );

  const updateCustomMutation = useMutation(
    (payload) => updateGateway(id, payload),
    {
      onSuccess: () => {
        toast({
          title: "تنظیمات ذخیره شد",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right"
        });
        queryClient.invalidateQueries(["gateway", id]);
      },
      onError: (err) => {
        toast({ title: "خطا", description: err.response?.data?.message || err.message, status: "error", duration: 4000, isClosable: true });
      },
    }
  );

  const refetchBasalamInfoMutation = useMutation(
    async () => {
      const res = await getBasalamCustomerId(gateway.basalamAccessToken, gateway.basalamRefreshToken);
      const vendorId = res?.data?.vendorId ?? res?.vendorId;
      const vendorIdentifier = res?.data?.vendorIdentifier ?? res?.vendorIdentifier;
      if (vendorId == null) throw new Error("آیدی غرفه در پاسخ سرور نبود");
      
      const payload = { basalamPanelId: String(vendorId) };
      if (vendorIdentifier) payload.basalamStoreId = String(vendorIdentifier);
      if (res?.data?.accessToken) payload.basalamAccessToken = res.data.accessToken;
      if (res?.data?.refreshToken) payload.basalamRefreshToken = res.data.refreshToken;
      
      await updateGateway(id, payload);
    },
    {
      onSuccess: () => {
        toast({ title: "اطلاعات باسلام با موفقیت به‌روزرسانی شد", status: "success", duration: 3000, isClosable: true });
        queryClient.invalidateQueries(["gateway", id]);
      },
      onError: (err) => {
        toast({ title: "خطا در دریافت اطلاعات", description: err.response?.data?.message || err.message, status: "error", duration: 4000, isClosable: true });
      }
    }
  );

  // Queries
  const { data: gatewayRes, isLoading: gatewayLoading, error: gatewayError } = useQuery(["gateway", id], () => getGateway(id), { enabled: !!id });
  const { data: statsRes, isLoading: statsLoading } = useQuery(["gateway-stats", id], () => getGatewayStats(id), { enabled: !!id });
  const { data: basalamWebhooksRes, isLoading: basalamWebhooksLoading, error: basalamWebhooksError } = useQuery(
    ["gateway-basalam-webhooks", id], () => getBasalamWebhooks(id), { enabled: !!id && gatewayRes?.data?.type === "basalam", retry: false }
  );

  const gateway = gatewayRes?.data;
  const statsData = statsRes?.data || {};
  const appStats = statsData.app || {};
  const digikalaStats = statsData.digikala || null;
  const basalamStats = statsData.basalam || null;
  const isLoading = gatewayLoading || statsLoading;

  useEffect(() => {
    if (gateway && gateway.type === "custom") {
      setCustomSiteUrl(gateway.customSiteUrl || "");
      setCustomCallbackUrl(gateway.customCallbackUrl || "");
      setCustomProductsUrl(gateway.customProductsUrl || "");
      setCustomStockUpdateUrl(gateway.customStockUpdateUrl || "");
      setIsDirty(false);
    }
  }, [gateway]);

  const handleCustomFieldChange = (setter) => (e) => {
    setter(e.target.value);
    setIsDirty(true);
  };

  const handleCopy = (text, title = "کپی شد") => {
    navigator.clipboard.writeText(text).then(
      () => toast({ title, status: "success", duration: 2000, position: "bottom-right" }),
      () => toast({ title: "خطا در کپی", status: "error", duration: 2000, position: "bottom-right" })
    );
  };

  function generateRandomString(length = 32) {
    try {
      if (window.crypto && window.crypto.getRandomValues) {
        const bytes = new Uint8Array(length);
        window.crypto.getRandomValues(bytes);
        return Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, length);
      }
    } catch (e) {}
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    for (let i = 0; i < length; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    return result;
  }

  // Errors & Loading States
  if (gatewayError) {
    return (
      <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage} p={{ base: 4, md: 8 }}>
        <Alert status="error" borderRadius="xl" boxShadow="sm">
          <AlertIcon />
          <Box>
            <AlertTitle>درگاه یافت نشد</AlertTitle>
            <AlertDescription>{gatewayError.response?.data?.message || gatewayError.message}</AlertDescription>
          </Box>
        </Alert>
        <Button mt={6} colorScheme="blue" alignSelf="start" onClick={() => navigate("/gateways")}>
          بازگشت به لیست درگاه‌ها
        </Button>
      </Flex>
    );
  }

  if (isLoading || !gateway) {
    return (
      <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage} align="center" justify="center">
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text mt={4} fontWeight="bold" color={textColor} animation="pulse">در حال بارگذاری اطلاعات درگاه...</Text>
      </Flex>
    );
  }

  const typeLabel = gateway.type === "basalam" ? "باسلام" : gateway.type === "digikala" ? "دیجی‌کالا" : "سایت شخصی";
  const typeColor = gateway.type === "basalam" ? "orange" : gateway.type === "digikala" ? "red" : "purple";

  return (
    <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage}>
      <Box maxW="7xl" mx="auto" w="full" p={{ base: 4, md: 8 }}>
        
        <Button variant="link" color={mutedColor} size="sm" mb={6} onClick={() => navigate("/gateways")} _hover={{ color: "blue.500", textDecoration: "none" }}>
          ← بازگشت به لیست درگاه‌ها
        </Button>

        {/* ========================================== */}
        {/* هدر اصلی پروفایل (Hero Section) */}
        {/* ========================================== */}
        <Box bg={cardBg} borderRadius="2xl" p={{ base: 5, md: 8 }} mb={8} borderWidth="1px" borderColor={borderColor} boxShadow="sm" position="relative" overflow="hidden">
          <Box position="absolute" top={0} left={0} w="120px" h="120px" bg={`${typeColor}.400`} filter="blur(80px)" opacity={0.15} rounded="full" />
          
          <Flex justify="space-between" align={{ base: "start", md: "center" }} flexDir={{ base: "column", md: "row" }} gap={6} position="relative" zIndex={1}>
            <HStack spacing={4}>
              <Flex w="60px" h="60px" borderRadius="xl" bg={`${typeColor}.50`} color={`${typeColor}.500`} align="center" justify="center" shadow="sm">
                <Icon as={FaStore} boxSize={7} />
              </Flex>
              <VStack align="start" spacing={1}>
                <HStack wrap="wrap">
                  <Heading size="lg" color={textColor} letterSpacing="tight">{gateway.name}</Heading>
                  <Badge colorScheme={gateway.isActive ? "green" : "gray"} borderRadius="full" px={3} py={0.5} ml={2}>
                    {gateway.isActive ? "فعال" : "غیرفعال"}
                  </Badge>
                  <Badge colorScheme={gateway.syncPrice !== false ? "purple" : "gray"} borderRadius="full" px={3} py={0.5}>
                    {gateway.syncPrice !== false ? "قیمت‌گذاری محصولات فعال" : "قیمت‌گذاری همگانی غیرفعال"}
                  </Badge>
                </HStack>
                <HStack>
                  <Badge colorScheme={typeColor} variant="subtle" borderRadius="md" px={2}>{typeLabel}</Badge>
                  <Text fontSize="sm" color={mutedColor}>اتصال و همگام‌سازی</Text>
                </HStack>
                <HStack spacing={2} pt={1} wrap="wrap">
                  <Text fontSize="xs" color={mutedColor}>قیمت‌گذاری خودکار محصولات روی این درگاه:</Text>
                  <Switch
                    size="sm"
                    colorScheme="purple"
                    isChecked={gateway.syncPrice !== false}
                    onChange={async (e) => {
                      try {
                        await updateGateway(id, { syncPrice: e.target.checked });
                        queryClient.invalidateQueries(["gateway", id]);
                        queryClient.invalidateQueries(["gateways"]);
                        toast({
                          title: e.target.checked ? "قیمت‌گذاری خودکار محصولات این درگاه فعال شد" : "قیمت‌گذاری خودکار محصولات این درگاه غیرفعال شد",
                          status: "success",
                          duration: 3000,
                          isClosable: true,
                        });
                      } catch (err) {
                        toast({
                          title: "خطا در تغییر وضعیت قیمت‌گذاری",
                          description: err.response?.data?.message || err.message,
                          status: "error",
                          duration: 4000,
                          isClosable: true,
                        });
                      }
                    }}
                  />
                  {gateway.syncPrice !== false && (
                    <HStack spacing={1}>
                      <Text fontSize="xs" color={mutedColor}>درصد همگانی:</Text>
                      <Badge colorScheme="purple" variant="subtle" borderRadius="md" px={2}>
                        {gateway.pricePercent ?? 0}٪
                      </Badge>
                      <Text fontSize="xs" color={mutedColor}>(از ویرایش درگاه قابل تغییر است)</Text>
                    </HStack>
                  )}
                </HStack>
              </VStack>
            </HStack>

            <Flex gap={3} flexWrap="wrap">
              {(gateway.type === "digikala" || gateway.type === "basalam" || gateway.type === "custom") && (
                <Button
                  leftIcon={<FaSync />}
                  bgGradient={`linear(to-r, ${typeColor}.500, ${typeColor}.400)`}
                  color="white"
                  _hover={{ bgGradient: `linear(to-r, ${typeColor}.600, ${typeColor}.500)`, transform: "translateY(-1px)", shadow: "md" }}
                  isLoading={importMutation.isLoading}
                  onClick={() => importMutation.mutate()}
                  borderRadius="xl"
                >
                  دریافت محصولات پنل
                </Button>
              )}
              {appStats.productsCount > 0 && (
                <Button
                  leftIcon={<FaUnlink />}
                  colorScheme="gray"
                  variant="outline"
                  borderRadius="xl"
                  isLoading={disconnectProductsMutation.isLoading}
                  onClick={() => {
                    if (window.confirm(`آیا از لغو اتصال ${appStats.productsCount} محصول اطمینان دارید؟`)) {
                      disconnectProductsMutation.mutate();
                    }
                  }}
                >
                  لغو اتصال
                </Button>
              )}
            </Flex>
          </Flex>
        </Box>

        {/* هشدارها */}
        {gateway.type === "basalam" && (
          <Alert status="warning" borderRadius="xl" mb={8} bg="orange.50" borderWidth="1px" borderColor="orange.200" _dark={{ bg: "orange.900/30", borderColor: "orange.800" }}>
            <AlertIcon color="orange.500" />
            <Box>
              <AlertTitle fontWeight="bold">قانون باسلام برای کد حسابداری</AlertTitle>
              <AlertDescription fontSize="sm" mt={1}>
                کد محصول باید حتماً در <b>انتهای عنوان بدون فاصله</b> قرار بگیرد (مثال: کیف چرمی مدل L2190).
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {/* ========================================== */}
        {/* آمار سیستم مرکزی (CoreSell) */}
        {/* ========================================== */}
        <Box mb={10}>
          <Heading size="md" color={textColor} mb={2}>گزارش انبار مرکزی</Heading>
          <Text fontSize="sm" color={mutedColor} mb={5}>آمار ثبت شده درون دیتابیس اختصاصی شما</Text>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5}>
            <MiniStatCard title="محصولات متصل" value={appStats.productsCount ?? 0} icon={FaBoxOpen} colorScheme="blue" />
            <MiniStatCard title="تعداد سفارشات" value={appStats.salesCount ?? 0} icon={FaChartLine} colorScheme="teal" />
            <MiniStatCard title="مجموع واحدهای فروش" value={appStats.totalQuantitySold ?? 0} icon={FaShoppingCart} colorScheme="purple" />
            <MiniStatCard title="آخرین سفارش" value={appStats.lastSaleAt ? new Date(appStats.lastSaleAt).toLocaleDateString("fa-IR") : "—"} icon={FaCalendarAlt} colorScheme="orange" />
          </SimpleGrid>
        </Box>

        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8} mb={10}>
          {/* ========================================== */}
          {/* تنظیمات وب‌هوک و اتصال (سایت شخصی / دیجی‌کالا / باسلام) */}
          {/* ========================================== */}
          <GridItem>
            <Box bg={cardBg} borderRadius="2xl" p={6} borderWidth="1px" borderColor={borderColor} shadow="sm" h="full">
              <HStack mb={6}>
                <Icon as={FaLink} color="blue.500" boxSize={5} />
                <Heading size="md" color={textColor}>تنظیمات اتصال (وب‌هوک)</Heading>
              </HStack>

              {(gateway.type === "digikala" || gateway.type === "basalam") ? (
                <VStack align="stretch" spacing={4}>
                  <Text fontSize="sm" color={mutedColor}>این آدرس را در پنل {typeLabel} به‌عنوان URL وب‌هوک سفارشات ثبت کنید.</Text>
                  <Box position="relative">
                    <Code display="block" p={4} borderRadius="xl" fontSize="sm" bg={codeBg} color={textColor} wordBreak="break-all" borderWidth="1px" borderColor={borderColor}>
                      {`${process.env.REACT_APP_API_URL || window.location.origin + "/api"}/webhooks/${gateway.type === "digikala" ? "digikala" : "basalam"}/${id}`}
                    </Code>
                    <Button size="sm" position="absolute" top={2} left={2} leftIcon={<FaCopy />} onClick={() => handleCopy(`${process.env.REACT_APP_API_URL || window.location.origin + "/api"}/webhooks/${gateway.type === "digikala" ? "digikala" : "basalam"}/${id}`, "URL وب‌هوک کپی شد")}>کپی</Button>
                  </Box>
                </VStack>
              ) : (
                <VStack align="stretch" spacing={6}>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>URL دریافت سفارشات (Webhook)</Text>
                    <InputGroup size="md">
                      <Input readOnly value={`${process.env.REACT_APP_API_URL || window.location.origin + "/api"}/webhooks/custom/${id}`} bg={codeBg} dir="ltr" fontSize="sm" />
                      <InputRightAddon cursor="pointer" onClick={() => handleCopy(`${process.env.REACT_APP_API_URL || window.location.origin + "/api"}/webhooks/custom/${id}`)} _hover={{ bg: "gray.200" }} children={<FaCopy color="gray" />} />
                    </InputGroup>
                  </Box>

                  <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>آدرس سایت (Site URL)</Text>
                    <Input size="md" value={customSiteUrl} onChange={handleCustomFieldChange(setCustomSiteUrl)} placeholder="https://your-site.com" dir="ltr" fontSize="sm" />
                  </Box>

                  <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>آدرس کال‌بک (Callback URL)</Text>
                    <Input size="md" value={customCallbackUrl} onChange={handleCustomFieldChange(setCustomCallbackUrl)} placeholder="https://your-site.com/api/payment/callback" dir="ltr" fontSize="sm" />
                  </Box>

                  <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>API دریافت محصولات فروشگاه</Text>
                    <Input size="md" value={customProductsUrl} onChange={handleCustomFieldChange(setCustomProductsUrl)} placeholder="https://example.com/api/products" dir="ltr" fontSize="sm" />
                  </Box>

                  <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>API تغییر موجودی فروشگاه</Text>
                    <Input size="md" value={customStockUpdateUrl} onChange={handleCustomFieldChange(setCustomStockUpdateUrl)} placeholder="https://example.com/api/stock/update" dir="ltr" fontSize="sm" />
                  </Box>

                  <Divider borderColor={borderColor} />

                  <Box>
                    <Flex justify="space-between" align="center" mb={2}>
                      <Text fontSize="sm" fontWeight="bold">API Key (X-Api-Key)</Text>
                      <Button size="xs" colorScheme="orange" variant="ghost" onClick={() => {
                        updateCustomMutation.mutate({ customWebhookApiKey: generateRandomString(24), customWebhookSecret: generateRandomString(64) });
                      }}>تولید کلید جدید</Button>
                    </Flex>
                    <InputGroup size="md">
                      <Input readOnly value={gateway.customWebhookApiKey || "—"} bg={codeBg} dir="ltr" fontSize="sm" />
                      <InputRightAddon cursor="pointer" onClick={() => handleCopy(gateway.customWebhookApiKey)} children={<FaCopy color="gray" />} />
                    </InputGroup>
                  </Box>

                  <Button colorScheme="blue" borderRadius="xl" size="lg" isLoading={updateCustomMutation.isLoading} onClick={() => updateCustomMutation.mutate({ customSiteUrl, customCallbackUrl, customProductsUrl, customStockUpdateUrl })}>
                    ذخیره تنظیمات سایت شخصی
                  </Button>
                </VStack>
              )}
            </Box>
          </GridItem>

          {/* ========================================== */}
          {/* آمارهای اختصاصی درگاه (دیجی‌کالا یا باسلام) */}
          {/* ========================================== */}
          <GridItem>
             {gateway.type === "digikala" && (
                <Box bg={cardBg} borderRadius="2xl" p={6} borderWidth="1px" borderColor={borderColor} shadow="sm" h="full">
                  <HStack mb={6}>
                    <Icon as={FaCogs} color="red.500" boxSize={5} />
                    <Heading size="md" color={textColor}>وضعیت زنده دیجی‌کالا</Heading>
                  </HStack>

                  {digikalaStats?.error ? (
                    <Alert status="error" borderRadius="xl"><AlertIcon />{digikalaStats.error}</Alert>
                  ) : (
                    <VStack align="stretch" spacing={5}>
                      <SimpleGrid columns={2} spacing={4}>
                        <Box p={4} rounded="xl" bg="red.50" _dark={{ bg: "whiteAlpha.100" }}>
                          <Text fontSize="xs" color="red.600" _dark={{ color: "red.300" }} fontWeight="bold">تنوع‌های فعال</Text>
                          <Text fontSize="2xl" fontWeight="black" color={textColor}>{digikalaStats?.variantsCount ?? "—"}</Text>
                        </Box>
                        <Box p={4} rounded="xl" bg="red.50" _dark={{ bg: "whiteAlpha.100" }}>
                          <Text fontSize="xs" color="red.600" _dark={{ color: "red.300" }} fontWeight="bold">سفارشات جاری</Text>
                          <Text fontSize="2xl" fontWeight="black" color={textColor}>{digikalaStats?.ordersCount ?? "—"}</Text>
                        </Box>
                      </SimpleGrid>
                      
                      <Divider borderColor={borderColor} />
                      
                      <Box>
                        <Text fontSize="sm" fontWeight="bold" mb={3}>مدیریت سفارشات پنل</Text>
                        <HStack>
                          <Button size="sm" colorScheme="red" variant="outline" onClick={() => navigate(`/gateways/${id}/digikala-orders`)}>همه سفارشات</Button>
                          <Button size="sm" colorScheme="red" onClick={() => navigate(`/gateways/${id}/digikala-orders-requiring-stock`)}>نیازمند تأمین</Button>
                        </HStack>
                      </Box>

                      <Box p={4} borderWidth="1px" borderColor={borderColor} rounded="xl">
                        <HStack justify="space-between">
                          <Text fontSize="sm" color={mutedColor}>اعتبار Access Token:</Text>
                          <Text fontSize="sm" fontWeight="bold" dir="ltr">{formatDateShamsiShort(digikalaStats?.accessTokenExpiresAt)}</Text>
                        </HStack>
                      </Box>
                    </VStack>
                  )}
                </Box>
             )}

              {gateway.type === "basalam" && (
                <Box bg={cardBg} borderRadius="2xl" p={6} borderWidth="1px" borderColor={borderColor} shadow="sm" h="full">
                  <Flex justify="space-between" align="center" mb={6}>
                    <HStack>
                      <Icon as={FaCogs} color="orange.500" boxSize={5} />
                      <Heading size="md" color={textColor}>وضعیت زنده باسلام</Heading>
                    </HStack>
                    <Button 
                      size="sm" colorScheme="orange" variant="outline" 
                      isLoading={refetchBasalamInfoMutation.isLoading}
                      onClick={() => refetchBasalamInfoMutation.mutate()}
                    >
                      گرفتن مجدد اطلاعات
                    </Button>
                  </Flex>

                  {basalamStats?.error ? (
                    <Alert status="error" borderRadius="xl"><AlertIcon />{basalamStats.error}</Alert>
                  ) : (
                    <VStack align="stretch" spacing={5}>
                      <SimpleGrid columns={2} spacing={4}>
                        <Box p={4} rounded="xl" bg="orange.50" _dark={{ bg: "whiteAlpha.100" }}>
                          <Text fontSize="xs" color="orange.600" _dark={{ color: "orange.300" }} fontWeight="bold">محصولات غرفه</Text>
                          <Text fontSize="2xl" fontWeight="black" color={textColor}>{basalamStats?.productsCount ?? "—"}</Text>
                        </Box>
                        <Box p={4} rounded="xl" bg="orange.50" _dark={{ bg: "whiteAlpha.100" }}>
                          <Text fontSize="xs" color="orange.600" _dark={{ color: "orange.300" }} fontWeight="bold">سفارشات غرفه</Text>
                          <Text fontSize="2xl" fontWeight="black" color={textColor}>{basalamStats?.parcelsCount ?? "—"}</Text>
                        </Box>
                      </SimpleGrid>
                      
                      <Divider borderColor={borderColor} />
                      
                      <Box>
                        <Text fontSize="sm" fontWeight="bold" mb={2}>لاگ اتصال وب‌هوک (موقت)</Text>
                        <Box maxH="120px" overflow="auto" p={3} bg={codeBg} borderRadius="xl" fontSize="xs" fontFamily="mono" dir="ltr" borderWidth="1px" borderColor={borderColor}>
                           {basalamWebhooksLoading ? <Spinner size="sm" /> : <pre>{JSON.stringify(basalamWebhooksRes?.data ?? {}, null, 2)}</pre>}
                        </Box>
                      </Box>
                    </VStack>
                  )}
                </Box>
             )}
             
             {gateway.type === "custom" && (
                <Box bg={cardBg} borderRadius="2xl" p={6} borderWidth="1px" borderColor={borderColor} shadow="sm" h="full" display="flex" alignItems="center" justifyContent="center" textAlign="center">
                  <VStack spacing={3} opacity={0.6}>
                     <Icon as={FaKey} boxSize={10} color={mutedColor} />
                     <Text>سایت‌های شخصی مستقیماً با دیتابیس شما همگام هستند و نیازی به دریافت آمار خارجی ندارند.</Text>
                  </VStack>
                </Box>
             )}
          </GridItem>
        </Grid>

      </Box>

      {/* Floating Action Button (FAB) برای ذخیره تنظیمات درگاه */}
      {isDirty && gateway?.type === "custom" && (
        <Box
          position="fixed"
          bottom={8}
          left={8}
          zIndex={1000}
          animation="pulse 2s infinite"
          sx={{
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(49, 151, 149, 0.7)' },
              '70%': { transform: 'scale(1.05)', boxShadow: '0 0 0 15px rgba(49, 151, 149, 0)' },
              '100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(49, 151, 149, 0)' },
            }
          }}
        >
          <Button
            size="lg"
            colorScheme="teal"
            shadow="dark-lg"
            borderRadius="full"
            px={8}
            isLoading={updateCustomMutation.isLoading}
            onClick={() => {
              updateCustomMutation.mutate({ customSiteUrl, customCallbackUrl, customProductsUrl, customStockUpdateUrl });
              setIsDirty(false);
            }}
          >
            تغییرات سایت شخصی را ثبت کنید
          </Button>
        </Box>
      )}
    </Flex>
  );
}